import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Prop, Ref } from "@eastsideco/salvo-ts";
import { elevate, FacetParams, type LandingPage, type SortType } from '@apptus/esales-api';
import { VoyadoProductCard } from "./VoyadoProductCard";
import { VoyadoFilters } from "./VoyadoFilters";
import { resetElevateSessionIfTrackingConsentDenied } from "@/utils/elevate";

declare global {
    interface Window {
        elevate: ReturnType<typeof elevate>;
    }
}

@Component
export class VoyadoCollection extends ThemeComponent {
    @Ref grid!: HTMLDivElement;
    @Ref loadPrevious!: HTMLButtonElement;
    @Ref loadNext!: HTMLButtonElement;
    @Ref productCount!: HTMLSpanElement;
    @Ref filterBar!: VoyadoFilters;
    @Ref loading!: HTMLElement;

    @Prop({ default: 16 }) limit!: number;

    private _page: number = 1;
    private result?: LandingPage;
    private loadedPages: number[] = [];
    private loadedProductCount = 0;
    private totalProductCount = 0;
    private facets?: FacetParams;
    private sort?: SortType;

    set page(value: number) {
        this._page = value;

        const url = new URL(window.location.href);
        url.searchParams.set('page', this.page.toString());
        history.replaceState({}, "", url);

        this.loadedPages.push(value);

        this.setButtonVisibility();
    }

    get page() {
        return this._page;
    }

    get skip() {
        if (! this.page) {
            this.page = 1;
        }

        const skip = (this.limit * (this.page - 1) - (this.hasSmallBanner ? 1 : 0));
        return skip >= 0 ? skip : 0;
    }

    get previousPage() {
        const earliestPage = Math.min(...this.loadedPages);
        return earliestPage > 1 ? earliestPage - 1 : null;
    }

    get nextPage() {
        const lastPage = Math.max(...this.loadedPages);
        return lastPage + 1;
    }

    get progressLabel() {
        return `Showing ${this.loadedProductCount} of ${this.totalProductCount}`;
    }

    get progressPercent() {
        return (100 * this.loadedProductCount) / this.totalProductCount;
    }

    get hasLoadMore() {
        const [lastPage] = this.loadedPages.sort().reverse();

        if (this.hasSmallBanner) {
            return (this.limit * lastPage - 1) < this.totalProductCount;
        }

        return (this.limit * lastPage) < this.totalProductCount;
    }

    get hasSmallBanner() {
        return document.querySelector('.voyado-collection__banners .product-grid__product-item--small') != null;
    }

    init() {
        this.setInitialPage();

        const interval = setInterval(() => {
            // window.elevate needs to be defined by the Voyado
            // Elevate app before the collection can be loaded.
            if (!window.elevate) {
                return;
            }

            void this.fetchCollection();
            this.addLoadMoreListeners();
            this.addFilterListeners();

            clearInterval(interval);
        }, 200);
    }

    setInitialPage() {
        const params = new URLSearchParams(document.location.search);
        this.page = parseInt(params.get("page") || "1", 10);
    }

    async fetchCollection(insert: 'replace' | 'prepend' | 'append' = 'replace', resetCount = false) {
        if (! window.elevate) {
            return;
        }

        this.loading.classList.add('loading-spinner--active');

        const limit = (this.page == 1 && this.hasSmallBanner) ? this.limit - 1 : this.limit;

        resetElevateSessionIfTrackingConsentDenied();

        this.result = await window.elevate.query.landingPage({
            pageReference: `/${window.location.pathname.split('/').reverse()[0]}`,
            limit,
            skip: this.skip,
            sort: this.sort,
            facets: this.facets,
        });

        this.loading.classList.remove('loading-spinner--active');

        this.updateProductCounts(resetCount);
        this.showCollection(insert);
        this.setButtonVisibility();

        this.filterBar.setResult(this.result);
    }

    updateProductCounts(resetCount: boolean) {
        if (!this.result?.primaryList) {
            return;
        }

        this.loadedProductCount =
            (resetCount ? 0 : this.loadedProductCount) + this.result.primaryList.productGroups.length;
        this.totalProductCount = this.result.primaryList.totalHits;

        this.productCount.innerText = this.progressLabel;
        this.style.setProperty('--progress', `${this.progressPercent}%`);

    }

    showCollection(insert: 'replace' | 'prepend' | 'append' = 'replace') {
        if (!this.result?.primaryList?.productGroups) {
            return;
        }

        const documentFragment = document.createDocumentFragment();

        for (const productGroup of this.result.primaryList.productGroups) {
            const card = new VoyadoProductCard;
            card.productGroup = productGroup;

            documentFragment.appendChild(card);
        }

        const largeBanner = document.querySelector(
            '.voyado-collection__banners .product-grid__product-item--full'
        ) as HTMLElement;

        const smallBanner = document.querySelector(
            '.voyado-collection__banners .product-grid__product-item--small'
        ) as HTMLElement;

        switch (insert) {

            case 'replace':
                this.grid.replaceChildren(documentFragment);

                if (this.page == 1) {
                    if (largeBanner) {
                        const position = Number(largeBanner.dataset.position);
                        const referenceNode = this.grid.children[position];

                        this.grid.insertBefore(largeBanner.cloneNode(true), referenceNode);
                    }

                    if (smallBanner) {
                        const position = Number(smallBanner.dataset.position);
                        const referenceNode = this.grid.children[position];

                        this.grid.insertBefore(smallBanner.cloneNode(true), referenceNode);
                    }
                }

                break;
            case 'prepend':
                this.grid.prepend(documentFragment);

                if (this.page == 1) {
                    if (largeBanner) {
                        const position = Number(largeBanner.dataset.position);
                        const referenceNode = this.grid.children[position];

                        this.grid.insertBefore(largeBanner.cloneNode(true), referenceNode);
                    }

                    if (smallBanner) {
                        const position = Number(smallBanner.dataset.position);
                        const referenceNode = this.grid.children[position];

                        this.grid.insertBefore(smallBanner.cloneNode(true), referenceNode);
                    }
                }

                break;
            case 'append':
                this.grid.append(documentFragment);

                if (this.page == 1) {
                    if (largeBanner) {
                        const position = Number(largeBanner.dataset.position);
                        const referenceNode = this.grid.children[position];

                        this.grid.insertBefore(largeBanner.cloneNode(true), referenceNode);
                    }

                    if (smallBanner) {
                        const position = Number(smallBanner.dataset.position);
                        const referenceNode = this.grid.children[position];

                        this.grid.insertBefore(smallBanner.cloneNode(true), referenceNode);
                    }
                }

                break;
        }
    }

    setButtonVisibility() {
        this.loadPrevious.classList.toggle('voyado-collection__load-more--hidden', this.previousPage === null);
        this.loadNext.classList.toggle('voyado-collection__load-more--hidden', !this.hasLoadMore);
    }

    addLoadMoreListeners() {
        this.$listen(this.loadPrevious, 'click', () => {
            if (this.previousPage != null) {
                this.page = this.previousPage;
                void this.fetchCollection('prepend');
            }
        });

        this.$listen(this.loadNext, 'click', () => {
            this.page = this.nextPage;

            void this.fetchCollection('append');
        });
    }

    addFilterListeners() {
        this.$subscribe(this.filterBar.onChange, () => {
            const values = this.filterBar.values;

            this.sort = values.sortBy;
            this.facets = values.facetParams;

            this.loadedPages = [];
            this.page = 1;

            void this.fetchCollection('replace', true);
        });
    }
}
